import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import 'react-dates-17/initialize';
import 'react-dates-17/lib/css/_datepicker.css';
import { DayPickerRangeController } from 'react-dates-17';

import { setCheckIn, setCheckOut } from 'bookingform';
import { DEVICES, getHotelMinStayCalendar, startSearch } from '../../../actions/Booking';
import withNavigate from '../../../withNavigate'; // Importa el HOC

const today = moment().startOf('day');
const DATE_MASK = 'YYYY-MM-DD';
const DATE_MASK_PRINT = 'DD/MM/YYYY';

const outsideRangeDays = (day) => day.isBefore(today);

class MinimumStaysCalendar extends Component {
  constructor(props) {
    super(props);
    const startDate = moment(props.checkIn).startOf('month');
    this.state = {
      isLoading: true,
      isSearching: false,
      // "dayReloadTrick" is a value which we will ensure changes after each data loading
      // We will use this changing value on the component "Day" elements to force then to be redrawn
      dayReloadTrick: true,
      startDate,
      endDate: null,
      focusedInput: 'startDate',
      enabledButton: false,
      firstMonth: startDate,
      minStayNights: 1,
      daysInfo: {},
    };

    this.onFocusedInput = this.onFocusedInput.bind(this);
    this.onDatesChanged = this.onDatesChanged.bind(this);
    this.onSelectedMonthChange = this.onSelectedMonthChange.bind(this);
    this.renderDayContents = this.renderDayContents.bind(this);
  }

  componentDidMount(): void {
    this.loadMinStays(this.state.firstMonth);
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillUpdate(newProps) {
    if (this.props.isAvailLoading && !newProps.isAvailLoading) {
      // We want to stop the spinner when the search ends
      this.setState({ isLoading: false, isSearching: false });
      const roomsPath = this.props.isFreeModeSelected ? '/all-rooms/' : '/rooms/';
      this.props.navigate(roomsPath);
    }
  }

  loadMinStays(firstMonth) {
    this.setState({ isLoading: true });
    const calendarFrom = this.formatDate(firstMonth);
    this.props.dispatch(getHotelMinStayCalendar(this.props.hotelCode, calendarFrom)).then((daysInfo) => {
      // We want to keep all days in the calendar, to avoid loading them multiple times
      const allDays = { ...this.state.daysInfo, ...daysInfo };
      this.setState({
        isLoading: false, daysInfo: allDays, firstMonth, dayReloadTrick: !this.state.dayReloadTrick,
      });
    });
  }

  formatDate(date) {
    return date.format(DATE_MASK);
  }

  printDate(date) {
    return date.format(DATE_MASK_PRINT);
  }

  onSelectedMonthChange(newMonth) {
    const monthDate = this.formatDate(newMonth);
    const nextMonth = this.formatDate(moment(newMonth).add(1, 'month').endOf('month'));
    if (this.state.daysInfo[monthDate] && this.state.daysInfo[nextMonth]) {
      // Both next two months have already been loaded, so we just need to change the month and re-render
      this.setState({ firstMonth: newMonth, dayReloadTrick: !this.state.dayReloadTrick });
    } else {
      // One or two of the months have not been loaded yet
      this.loadMinStays(newMonth);
    }
  }

  onNewSearch() {
    this.props.dispatch(setCheckIn(this.formatDate(this.state.startDate)));
    this.props.dispatch(setCheckOut(this.formatDate(this.state.endDate)));
    this.props.dispatch(startSearch(this.context, this.props.navigate));
    this.setState({ isSearching: true });
  }

  onSelectHotel() {
    this.props.navigate('/all-rooms/');
  }

  onFocusedInput(whatIsFocused) {
    this.setState({
      focusedInput: whatIsFocused || 'startDate',
    });
  }

  onDatesChanged({ startDate, endDate }) {
    // We calculate the new date selection
    const isSettingBoth = startDate && endDate;
    const hadSetBoth = this.state.startDate && this.state.endDate;

    // In case that for the checkIn date there a minimum stay, we also update the calendar prop for min days selection
    const dayKey = this.formatDate(startDate);
    const checkoutDate = isSettingBoth && hadSetBoth ? null : endDate;

    // If they select at least one unavailable day, the "check availability" button will be disabled
    let hasDisabledDays = false;
    if (checkoutDate) {
      let testDate = moment(startDate);
      while (testDate.isBefore(checkoutDate)) {
        const testDateKey = this.formatDate(testDate);
        const testDayInfo = this.state.daysInfo[testDateKey];
        if (testDayInfo && !testDayInfo.available) {
          hasDisabledDays = true;
        }
        testDate = testDate.add(1, 'days');
      }
    }

    const dayInfo = this.state.daysInfo[dayKey] || { stays: 1 };
    this.setState({
      startDate,
      endDate: checkoutDate,
      minStayNights: dayInfo.stays > 1 ? dayInfo.stays : this.state.minStayNights,
      enabledButton: startDate && checkoutDate && !hasDisabledDays,
    });
  }

  renderDayContents(day, mods) {
    const dayKey = this.formatDate(day);
    const dayInfo = this.state.daysInfo[dayKey] || { available: true, stays: 1 };
    const showStays = dayInfo.stays > 1 && !mods.has('blocked');
    return (
      <div className={`stay-day ${dayInfo.available ? '' : 'stay-day--unavailable'}`}>
        {day.format('D')}
        {showStays && <div className="stay-day__min-day">{dayInfo.stays}</div>}
        {dayInfo.amount && <div className="stay-day__price">{dayInfo.amount}</div>}
      </div>
    );
  }

  renderLoader() {
    return (
      <div className="loader-wrapper">
        <div className="loader-sandos">
          {this.context.t('Loading')}...
        </div>
      </div>
    );
  }

  renderLegendPanel() {
    return (
      <legend className="legend-min-stay">
        <div className="legend-min-stay__option">
          <span className="color color--minstay" />
          {this.context.t('Minimum of nights')}
        </div>
        <div className="legend-min-stay__option">
          <span className="color color--unavailable mr-1" />
          <span className="color color--disabled" />
          {this.context.t('Not available')}
        </div>
        <div className="legend-min-stay__option">
          <span className="color color--selected" />
          {this.context.t('Selected')}
        </div>
      </legend>
    );
  }

  renderCalendar() {
    // We force "daySize" to change every time we receive new data to force days re-rendering via "renderDayContents"
    return (
      <DayPickerRangeController
        numberOfMonths={this.props.isDesktopDevice ? 2 : 1}
        firstDayOfWeek={1}
        hideKeyboardShortcutsPanel
        isOutsideRange={outsideRangeDays}
        minimumNights={this.state.minStayNights}
        daySize={65 + (this.state.dayReloadTrick ? 1 : 0)}
        startDate={this.state.startDate}
        endDate={this.state.endDate}
        onDatesChange={this.onDatesChanged}
        focusedInput={this.state.focusedInput}
        onFocusChange={this.onFocusedInput}
        onPrevMonthClick={this.onSelectedMonthChange}
        onNextMonthClick={this.onSelectedMonthChange}
        renderDayContents={this.renderDayContents}
        renderCalendarInfo={() => this.renderLegendPanel()}
      />
    );
  }

  renderRestrictionsButton() {
    return (
      <button className="btn btn-primary ml-4 py-1 font-weight-bold" onClick={() => this.onSelectHotel()}>
        {this.context.t('Omit restrictions and proceed to rooms')}
      </button>
    );
  }

  render() {
    if (this.state.isSearching) {
      return this.renderLoader();
    }
    return (
      <div className="hotel-alternative sandbox border border-primary">
        <div className="hotel-alternative__content">
          <h1 className="mt-0">{this.context.t('Hotel Calendar')}</h1>
          <h2 className="mb-2">
            {this.context.t('Your last search returned no results. Adjust the stay dates using the calendar below')}
          </h2>
          <p className="last-search">
            {this.context.t('Latest search was for')}
            <span className="number--big font-weight-bold">{this.props.nights}</span>
            {this.context.t('nights from')}
            <span className="number--big font-weight-bold">{this.printDate(moment(this.props.checkIn))}</span>
            {this.context.t('to')}
            <span className="number--big font-weight-bold">{this.printDate(moment(this.props.checkOut))}</span>
            {this.props.omitRestrictions && this.renderRestrictionsButton()}
          </p>
          <section className="hotel-alternative__calendar">
            {this.state.isLoading && this.renderLoader()}
            {this.renderCalendar()}
          </section>
          <button className="sandbox__btn search-min-stay"
                  disabled={!this.state.enabledButton} onClick={() => this.onNewSearch()}>
            {this.context.t('Check availability')}
          </button>
        </div>
      </div>
    );
  }
}

MinimumStaysCalendar.contextTypes = {
  t: PropTypes.func.isRequired,
};

MinimumStaysCalendar.propTypes = {
  checkIn: PropTypes.string.isRequired,
  hotelCode: PropTypes.string.isRequired,
  omitRestrictions: PropTypes.bool,
  navigate: PropTypes.func.isRequired, // Prop para `navigate`
};

MinimumStaysCalendar.defaultProps = {
  omitRestrictions: true,
};

// Conectar el componente con Redux
const mapStateToProps = (state) => ({
  availMinStay: state.booking.availMinStay,
  hotel: state.booking.hotel,
  checkIn: state.bookingFormState.checkIn,
  checkOut: state.bookingFormState.checkOut,
  isFreeModeSelected: state.bookingFormState.isFreeModeSelected,
  isDesktopDevice: state.booking.device === DEVICES.DESKTOP,
  nights: state.booking.last_requested_info.nights,
  isAvailLoading: state.booking.availLoading,
});

const ConnectedMinimumStaysCalendar = connect(mapStateToProps)(MinimumStaysCalendar);

// Exportar el componente envuelto con el HOC
export default withNavigate(ConnectedMinimumStaysCalendar);
